import { useState, useEffect } from 'react';
import API from './api';

const DEFAULTS = {
  landingStats: [
    { stat: '10+',   label: 'Languages Supported' },
    { stat: '4.9 ★', label: 'Average Rating' },
    { stat: '< 15s', label: 'Story Ready In' },
    { stat: '50+',   label: 'Themes & Topics' },
  ],
  testimonials: [
    { name: 'Sarah M.',  role: 'Mom of 2, London',     avatar: 'S', color: '#6f42c1', quote: 'My daughter asks for "her story" every single night now. Dreamy Bedtime has made bedtime the highlight of our entire day.' },
    { name: 'James T.',  role: 'Dad of 3, New York',   avatar: 'J', color: '#0d6efd', quote: "I used to dread making up stories after a long day. Now I just tap Generate and I'm suddenly the best storyteller in the world." },
    { name: 'Priya K.',  role: 'Mom of twins, Mumbai', avatar: 'P', color: '#dc3545', quote: 'Both my twins have completely different interests and Dreamy Bedtime nails both of them every single night. It genuinely feels like magic.' },
    { name: 'Lena H.',   role: 'Mom of 1, Berlin',     avatar: 'L', color: '#198754', quote: "We're a bilingual German-English family. Being able to generate stories in German means my mum in Stuttgart can read them too." },
    { name: 'Carlos R.', role: 'Dad of 2, Madrid',     avatar: 'C', color: '#fd7e14', quote: "The illustrations blew me away. My son now has a gallery on his bedroom wall — every story has its own piece of art." },
    { name: 'Aisha B.',  role: 'Grandma, Toronto',     avatar: 'A', color: '#e91e63', quote: "I gifted a story to my granddaughter for her birthday. Her parents said she asked to read it three nights in a row." },
  ],
  landingFeatures: [
    { icon: '✨', bg: '#fff0f6', title: 'Fully Personalised Stories',     desc: 'Every story stars your child by name, includes their hobbies, and weaves in the life lessons you choose.' },
    { icon: '🎙️', bg: '#f0e8ff', title: 'Narration & Your Own Voice',     desc: 'Paid plans get high-quality narration. Family plan lets you clone your own voice in 30 seconds.' },
    { icon: '🎨', bg: '#fff8e1', title: 'Story Illustrations',            desc: 'Every story gets a unique illustration that brings the story to life. Download and share with family.' },
    { icon: '🌍', bg: '#e8fff0', title: '10+ Languages',                  desc: 'Generate stories in English, Spanish, French, Hindi, German, Portuguese, Arabic, Chinese, Japanese or Italian.' },
    { icon: '📚', bg: '#e8f4ff', title: 'Permanent Story Library',        desc: 'Every story you generate is saved forever. Filter by child, favourite, or language.' },
    { icon: '🎯', bg: '#fff0e8', title: 'Weekly Themes & Scheduler',      desc: 'Set a lesson for each day of the week. Stories automatically weave in today\'s theme.' },
    { icon: '🔗', bg: '#f0fff0', title: 'Grandparent Sharing Links',      desc: 'Share any story with family via a private link. They can react without needing an account.' },
    { icon: '📄', bg: '#f8f0ff', title: 'PDF Export',                     desc: 'Download any story as a beautifully formatted PDF — perfect for printing or scrapbooks.' },
    { icon: '🔔', bg: '#fffde8', title: 'Bedtime Reminder Notifications', desc: 'Set a daily reminder for story time. Get an email or push notification at your chosen time.' },
    { icon: '📧', bg: '#e8f0ff', title: 'Weekly Story Digest',            desc: 'A weekly email summarising each child\'s upcoming themes with one-click Generate buttons.' },
    { icon: '🎁', bg: '#fff0f8', title: 'Gift a Story',                   desc: 'Send a personalised bedtime story to any child as a one-time gift. No account needed.' },
    { icon: '👨‍👩‍👧‍👦', bg: '#f0f8ff', title: 'Multiple Child Profiles',       desc: 'Each child has their own profile with separate interests, themes, and story history.' },
    { icon: '🛡️', bg: '#fff7ed', title: 'Parental Controls',              desc: 'Strict Mode blocks scary content. Approval Queue lets you review every story first.' },
    { icon: '📚', bg: '#f0fdf4', title: 'Reading Age Calibration',        desc: 'Stories automatically match your child\'s reading level — short sentences for toddlers, richer for older readers.' },
  ],
  storyTones: [
    { value: 'magical',     icon: '✨', desc: 'Wands, spells & wonder' },
    { value: 'adventurous', icon: '🗺️', desc: 'Epic quests & discovery' },
    { value: 'funny',       icon: '😄', desc: 'Giggles & silly moments' },
    { value: 'calm',        icon: '🌙', desc: 'Peaceful & soothing' },
    { value: 'educational', icon: '📚', desc: 'Fun facts woven in' },
    { value: 'cozy',        icon: '🔥', desc: 'Warm & comforting' },
    { value: 'exciting',    icon: '⚡', desc: 'Fast-paced & thrilling' },
    { value: 'spooky',      icon: '👻', desc: 'Mildly spooky (age-safe)' },
  ],
  storyLanguages: [
    { code: 'en', label: 'English',    flag: '🇬🇧' },
    { code: 'es', label: 'Spanish',    flag: '🇪🇸' },
    { code: 'fr', label: 'French',     flag: '🇫🇷' },
    { code: 'hi', label: 'Hindi',      flag: '🇮🇳' },
    { code: 'gu', label: 'Gujarati',   flag: '🇮🇳' },
    { code: 'de', label: 'German',     flag: '🇩🇪' },
    { code: 'pt', label: 'Portuguese', flag: '🇧🇷' },
    { code: 'ar', label: 'Arabic',     flag: '🇸🇦' },
    { code: 'zh', label: 'Chinese',    flag: '🇨🇳' },
    { code: 'ja', label: 'Japanese',   flag: '🇯🇵' },
    { code: 'it', label: 'Italian',    flag: '🇮🇹' },
  ],
  storyInterests: ['Dinosaurs','Space','Animals','Magic','Superheroes','Princesses','Cars','Nature','Music','Ocean','Dragons','Robots'],
  learningCategories: [
    { label: 'Science & Nature',    icon: '🔬', topics: ['The Solar System','How Plants Grow','The Water Cycle','Volcanoes','Dinosaurs','Animals & Habitats','Weather & Seasons','The Human Body','Insects & Bugs','Ocean Life'] },
    { label: 'Maths',               icon: '🔢', topics: ['Counting & Numbers','Addition & Subtraction','Shapes & Geometry','Fractions','Telling Time','Measuring & Sizes','Patterns','Money & Coins'] },
    { label: 'Life Skills',         icon: '🌱', topics: ['Sharing & Taking Turns','Saying Sorry','Tidying Up','Washing Hands','Healthy Eating','Bedtime Routines','Road Safety','Stranger Danger','Managing Feelings'] },
    { label: 'History & Culture',   icon: '🏛️', topics: ['Ancient Egypt','Knights & Castles','Space Exploration','Famous Inventors','World Cultures','How Rainbows Form','Recycling & the Planet','Where Food Comes From'] },
    { label: 'Language & Literacy', icon: '📖', topics: ['Rhyming Words','The Alphabet','Opposites','Colours & Shades','Days of the Week','Months of the Year','Storytelling Structure'] },
  ],
  themeCategories: [
    { label: 'Life Lessons', icon: '💡', desc: 'Build character with timeless values', themes: ['Kindness','Bravery','Friendship','Sharing','Honesty','Patience','Curiosity','Gratitude','Perseverance','Empathy','Courage','Generosity'] },
    { label: 'Seasonal',     icon: '🎃', desc: 'Celebrate holidays and seasons',       themes: ['🎃 Halloween','🎄 Christmas','🌸 Spring','☀️ Summer','🍂 Autumn','❄️ Winter','🐣 Easter','🎆 New Year'] },
    { label: 'Emotional',    icon: '💛', desc: 'Help children process big feelings',   themes: ['New Sibling','Starting School','Fear of the Dark','Moving Home','Missing Someone','Making New Friends','Feeling Nervous','Losing a Pet','Dealing with Anger','Being Different'] },
  ],
  featureFlags: {
    community: true, photoIllustrations: true, giftStory: true,
    voiceCloning: true, storyQuiz: true, storySeries: true,
    pdfExport: true, grandparentSharing: true, weeklyDigest: true, bedtimeReminder: true,
  },
  maintenanceMode: { enabled: false, message: '' },
  contactResponseTime: '24–48 hours',
};

// No-op kept for backward compatibility with AdminSettings.js import
export const bustSettingsCache = () => {};

const useSettings = () => {
  const [settings, setSettings] = useState(DEFAULTS);
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    API.get('/settings')
      .then(res => {
        const incoming = res.data?.settings || {};
        setSettings({
          ...DEFAULTS,
          ...incoming,
          // featureFlags needs a DEEP merge. A shallow spread replaces the whole
          // object, so a stored value missing any key left that flag undefined —
          // falsy — and the feature vanished from the UI. That turns one
          // incomplete write into a site-wide disappearance of gift, voice
          // cloning, series and the rest.
          //
          // Merging key-by-key also matches how the server-side gate reads these:
          // requireFeature blocks only on an explicit `false`, treating a missing
          // key as "never configured". Frontend and backend now agree.
          featureFlags: { ...DEFAULTS.featureFlags, ...(incoming.featureFlags || {}) },
        });
      })
      .catch(() => setSettings(DEFAULTS))
      .finally(() => setLoading(false));
  }, []);

  return { settings, loading };
};

export default useSettings;
